import { Box, Divider, Heading, VStack } from '@chakra-ui/react';
import { CartTicketInfo } from 'context/CartContext/types';
import { FormattedMessage } from 'react-intl';
import { AcceptedPayment } from './AcceptedPayment/AcceptedPayment';
import { CartEmpty } from './CartEmpty/CartEmpty';
import { PleaseNote } from './PleaseNote/PleaseNote';
import { Ticket } from './Ticket/Ticket';
import { Total } from './Total/Total';
import { useAuthCode } from 'context/AuthCodeContext/AuthCodeContext';
import { OrphanSeatsContent } from './OrphanSeatsContent/OrphanSeatsContent';

interface TicketCartProps {
  selectedTickets?: CartTicketInfo[];
  isValidSelection: boolean;
  currency: string;
  eventId: number;
  isFreeEvent?: boolean;
}

export const TicketCart = ({
  selectedTickets,
  isValidSelection,
  currency,
  eventId,
  isFreeEvent,
}: TicketCartProps) => {
  const qiToken = window.qi.user?.mobile.token;
  const authCode = useAuthCode();

  const hasMandatory = selectedTickets?.some((t) => t.mandatory === true) ?? false;
  const hasNonMandatory = selectedTickets?.some((t) => t.mandatory === false) ?? false;
  const hasNonMandatoryWithoutMandatory = hasNonMandatory && !hasMandatory;

  return (
    // Cart internals were authored to look RTL under the app's default LTR
    // (manual textAlign='right' / justify ordering). The event pages now wrap
    // content in dir='rtl', which double-flips these rows — so pin the cart
    // subtree back to LTR to preserve its original, correct RTL appearance.
    <VStack dir='ltr' width='100%' height='100%' align='stretch' bg='white' p={0}>
      <Box width='100%'>
        <Heading p='16px' variant='headingSmall' textAlign='right'>
          <FormattedMessage id='eventpage.ticketcart.yourtickets' defaultMessage='Your tickets' />
        </Heading>
        <Divider colorScheme='BG100' />
      </Box>
      {!selectedTickets?.length ? (
        <CartEmpty />
      ) : !isValidSelection ? (
        <OrphanSeatsContent />
      ) : (
        <VStack
          width='100%'
          height='100%'
          align='stretch'
          justify='space-between'
          p={0}
          overflowY='auto'
        >
          <VStack align='stretch' p='24px' spacing='24px'>
            <VStack align='stretch'>
              {selectedTickets.map((ticket, idx) => (
                <Ticket
                  key={ticket.sector + idx}
                  title={ticket.sector}
                  category={ticket.category}
                  price={ticket.price}
                  currency={currency}
                  ticketType={ticket.ticketLabel ?? ticket.ticketType}
                  onDeleteClick={() => ticket.deselect()}
                />
              ))}
            </VStack>
            {!isFreeEvent && qiToken === undefined && authCode === null && <AcceptedPayment />}
            {!isFreeEvent && qiToken === undefined && authCode === null && <PleaseNote />}
          </VStack>
          <Total
            currency={currency}
            totalAmount={selectedTickets.reduce((acc, curr) => acc + curr.price, 0)}
            eventId={eventId}
            ticketQuantity={selectedTickets.length}
            isDisabled={hasNonMandatoryWithoutMandatory}
            isFreeEvent={isFreeEvent}
          />
        </VStack>
      )}
    </VStack>
  );
};
